import { apiSlice } from "@/redux/apiSlice";
import { ApiResponse } from "@/types";
import {
  MembershipFacilitySchedule,
  BulkMembershipFacilityScheduleCreate,
  MembershipFacilityScheduleFilters,
  MembershipFacilityWindow,
  MembershipAvailabilityCalendarParams,
  MembershipAvailabilityCalendarResponse,
  MembershipAvailabilityOptions,
  MembershipFacilityOccurrenceUpdate,
} from "@/types/membership-facility-schedule";

export const membershipFacilityScheduleApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMembershipFacilitySchedules: builder.query<
      MembershipFacilitySchedule[],
      MembershipFacilityScheduleFilters | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.membership_plan_id) {
          queryParams.append("membership_plan_id", params.membership_plan_id.toString());
        }
        if (params?.facility_id) {
          queryParams.append("facility_id", params.facility_id.toString());
        }
        if (params?.search_key) {
          queryParams.append("search_key", params.search_key);
        }
        const qs = queryParams.toString();
        return {
          url: `/membership_facility_schedule${qs ? `?${qs}` : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        };
      },
      providesTags: ["MembershipFacilitySchedule"],
    }),

    // Memberships and facilities for the Facility Availability screen. Served under that
    // screen's own permission, so it works without access to the Membership/Facility modules.
    getMembershipAvailabilityOptions: builder.query<
      MembershipAvailabilityOptions,
      { business_unit_id?: number; membership_plan_id?: number } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.business_unit_id) {
          queryParams.append("business_unit_id", params.business_unit_id.toString());
        }
        // Limits `facilities` to the ones attached to this membership.
        if (params?.membership_plan_id) {
          queryParams.append("membership_plan_id", params.membership_plan_id.toString());
        }
        const qs = queryParams.toString();
        return {
          url: `/membership_facility_schedule/options${qs ? `?${qs}` : ""}`,
          method: "GET",
          headers: { Accept: "application/json" },
        };
      },
      providesTags: ["MembershipFacilitySchedule", "Memberships", "Facility"],
    }),

    getMembershipAvailabilityCalendar: builder.query<
      MembershipAvailabilityCalendarResponse,
      MembershipAvailabilityCalendarParams
    >({
      query: (params) => {
        const queryParams = new URLSearchParams({
          start_date: params.start_date,
          end_date: params.end_date,
        });
        if (params.membership_plan_id) {
          queryParams.append("membership_plan_id", params.membership_plan_id.toString());
        }
        if (params.facility_id) queryParams.append("facility_id", params.facility_id.toString());
        if (params.business_unit_id) {
          queryParams.append("business_unit_id", params.business_unit_id.toString());
        }
        return {
          url: `/membership_facility_schedule/calendar?${queryParams.toString()}`,
          method: "GET",
          headers: { Accept: "application/json" },
        };
      },
      providesTags: ["MembershipFacilitySchedule"],
    }),

    createMembershipFacilitySchedule: builder.mutation<
      ApiResponse,
      MembershipFacilityWindow & {
        org_id: number;
        membership_plan_id: number;
        facility_id: number;
      }
    >({
      query: (data) => ({
        url: "/membership_facility_schedule",
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
        },
      }),
      invalidatesTags: ["MembershipFacilitySchedule"],
    }),

    updateMembershipFacilitySchedule: builder.mutation<
      ApiResponse,
      MembershipFacilityWindow & { id: number }
    >({
      query: ({ id, ...window }) => ({
        url: `/membership_facility_schedule/${id}`,
        method: "PUT",
        body: window,
        headers: {
          Accept: "application/json",
        },
      }),
      invalidatesTags: ["MembershipFacilitySchedule"],
    }),

    // One day of a window: give it different hours, or remove it (is_cancelled).
    setMembershipFacilityOccurrence: builder.mutation<ApiResponse, MembershipFacilityOccurrenceUpdate>({
      query: ({ id, date, ...body }) => ({
        url: `/membership_facility_schedule/${id}/occurrences/${date}`,
        method: "PUT",
        body,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["MembershipFacilitySchedule"],
    }),

    // Undo a one-day change so the day follows the window again.
    clearMembershipFacilityOccurrence: builder.mutation<ApiResponse, { id: number; date: string }>({
      query: ({ id, date }) => ({
        url: `/membership_facility_schedule/${id}/occurrences/${date}`,
        method: "DELETE",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["MembershipFacilitySchedule"],
    }),

    deleteMembershipFacilitySchedule: builder.mutation<ApiResponse, number>({
      query: (id) => ({
        url: `/membership_facility_schedule/${id}`,
        method: "DELETE",
        headers: {
          Accept: "application/json",
        },
      }),
      invalidatesTags: ["MembershipFacilitySchedule"],
    }),

    bulkSaveMembershipFacilitySchedules: builder.mutation<
      ApiResponse,
      {
        membership_plan_id: number;
        data: BulkMembershipFacilityScheduleCreate;
      }
    >({
      query: ({ membership_plan_id, data }) => ({
        url: `/membership_plan/${membership_plan_id}/facility_schedules`,
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
        },
      }),
      invalidatesTags: ["MembershipFacilitySchedule"],
    }),
  }),
});

export const {
  useGetMembershipFacilitySchedulesQuery,
  useGetMembershipAvailabilityCalendarQuery,
  useGetMembershipAvailabilityOptionsQuery,
  useCreateMembershipFacilityScheduleMutation,
  useUpdateMembershipFacilityScheduleMutation,
  useDeleteMembershipFacilityScheduleMutation,
  useSetMembershipFacilityOccurrenceMutation,
  useClearMembershipFacilityOccurrenceMutation,
  useBulkSaveMembershipFacilitySchedulesMutation,
} = membershipFacilityScheduleApi;
